import { createFileRoute, Outlet } from "@tanstack/react-router";
import { AdminPageHeader } from "@/components/admin/admin-page-header";
import { OnboardingNav } from "@/components/sites/onboarding-nav";
import { OnboardingResetProvider } from "@/components/sites/onboarding-reset";
import { ONBOARDING_STATUS_META } from "@/components/sites/onboarding-steps";
import { useOnboardingProgress } from "@/components/sites/use-onboarding-progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/onboarding")({
  component: OnboardingLayout,
  staticData: {
    title: "Onboarding",
  },
});

/**
 * The frame every onboarding screen shares: which asset is being onboarded,
 * how far it has got, and one click to any step.
 *
 * The pages below were three siblings that each re-declared their own header
 * and page column, which is how they drifted apart. Everything common lives
 * here now, so a page can only contribute its own content.
 */
function OnboardingLayout() {
  const { siteName, organizationName, isLoading, steps, status } =
    useOnboardingProgress();

  const {
    icon: StatusIcon,
    label: statusLabel,
    chip: statusChip,
  } = ONBOARDING_STATUS_META[status];

  return (
    <OnboardingResetProvider>
      <div className="@container/main flex flex-1 flex-col">
        {/* pb-28 clears the floating action bar on the upload screens. */}
        <div className="flex w-full flex-col gap-6 p-4 pb-28 lg:p-7 lg:pb-28">
          <div className="flex flex-col gap-4">
            <AdminPageHeader
              title="Site onboarding"
              description={
                isLoading ? (
                  <Skeleton className="h-4 w-56 bg-white/15" />
                ) : siteName ? (
                  [siteName, organizationName].filter(Boolean).join(" · ")
                ) : (
                  "No asset selected"
                )
              }
              trailing={
                /* Status carries icon + label, never colour alone (Color-Plus-Shape). */
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                    statusChip,
                  )}
                >
                  <StatusIcon
                    className="size-3.5"
                    strokeWidth={2}
                    aria-hidden
                  />
                  {statusLabel}
                </span>
              }
            />

            <OnboardingNav steps={steps} />
          </div>

          <Outlet />
        </div>
      </div>
    </OnboardingResetProvider>
  );
}
